import { httpsCallable } from 'firebase/functions';
import { Atom, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ANALYSIS_AXES, type AnalysisAxisId } from '@goenhub/shared';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMails } from '@/hooks/useMailData';
import { functions } from '@/lib/firebase';
import { cn } from '@/lib/utils';

interface AnalysisResponse {
  ok: boolean;
  analysisId: string;
  modelUsed: string;
  results: Record<
    string,
    {
      title: string;
      summary: string;
      details: string;
      recommendations: string[];
      score: number;
    }
  >;
}

const runAnalysisFn = httpsCallable<
  { mailId: string; axes: AnalysisAxisId[] },
  AnalysisResponse
>(functions, 'runMailAnalysis');

function scoreColor(score: number): string {
  if (score >= 90) return 'var(--accent-green)';
  if (score >= 70) return 'var(--accent-cyan)';
  if (score >= 50) return 'var(--accent-amber)';
  return 'var(--accent-red)';
}

export function MailAnalysisPage() {
  const { data: mails, isLoading } = useMails({ deleted: false, limit: 100 });
  const [selectedMailId, setSelectedMailId] = useState('');
  const [selectedAxes, setSelectedAxes] = useState<Set<AnalysisAxisId>>(
    new Set(['target', 'value', 'emotion']),
  );
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<AnalysisResponse['results'] | null>(null);

  const selectedMail = useMemo(
    () => mails?.find((m) => m.id === selectedMailId) ?? null,
    [mails, selectedMailId],
  );

  const toggleAxis = (id: AnalysisAxisId) => {
    const next = new Set(selectedAxes);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedAxes(next);
  };

  const handleRun = async () => {
    if (!selectedMailId) {
      toast.error('メールを選択してください');
      return;
    }
    if (selectedAxes.size === 0) {
      toast.error('分析軸を 1 つ以上選択してください');
      return;
    }
    setRunning(true);
    setResults(null);
    try {
      const res = await runAnalysisFn({
        mailId: selectedMailId,
        axes: Array.from(selectedAxes),
      });
      setResults(res.data.results);
      toast.success(`解析完了（${res.data.modelUsed}）`);
    } catch (err) {
      toast.error(`解析失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Screen
      title="意図設定（量子場解析）"
      description="選択した 8 軸でメール内容を AI 分析し、改善提案を生成します"
    >
      <div className="max-w-5xl mx-auto">
        {/* 分析対象選択 */}
        <div className="mb-6">
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] mb-1.5">
            分析対象メール
          </label>
          {isLoading ? (
            <Spinner />
          ) : (
            <Select value={selectedMailId} onValueChange={setSelectedMailId}>
              <SelectTrigger>
                <SelectValue placeholder="メールを選択してください" />
              </SelectTrigger>
              <SelectContent>
                {mails?.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    [{m.status}] {m.subject || '(件名なし)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedMail && (
            <div className="mt-2 p-3 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm text-xs">
              <div className="font-semibold mb-1">{selectedMail.subject}</div>
              <div className="text-[var(--text-muted)] line-clamp-2">
                {selectedMail.overview || selectedMail.sections?.[0]?.content?.slice(0, 120)}
              </div>
            </div>
          )}
        </div>

        {/* 8 軸選択 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              分析軸（複数選択可）
            </label>
            <span className="text-xs text-[var(--text-muted)]">
              選択中: {selectedAxes.size} / {ANALYSIS_AXES.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ANALYSIS_AXES.map((axis) => {
              const checked = selectedAxes.has(axis.id);
              return (
                <label
                  key={axis.id}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded border cursor-pointer transition-all',
                    checked
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                      : 'border-[var(--border)] bg-[var(--bg-surface)] hover:border-[var(--text-muted)]',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAxis(axis.id)}
                    className="mt-0.5 accent-[var(--accent)]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold mb-0.5">{axis.name}</div>
                    <div className="text-xs text-[var(--text-secondary)] leading-relaxed">
                      {axis.desc}
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">
                      {axis.category}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex justify-center mb-8">
          <Button
            type="button"
            variant="primary"
            onClick={handleRun}
            disabled={running || !selectedMailId || selectedAxes.size === 0}
          >
            <Atom />
            {running ? '解析中...' : '量子場解析を実行'}
          </Button>
        </div>

        {/* 結果表示 */}
        {running && (
          <div className="py-12 flex flex-col items-center gap-3">
            <Spinner className="size-8 border-[3px]" />
            <p className="text-xs text-[var(--text-muted)]">Claude Opus が多角的に分析中...</p>
          </div>
        )}

        {results && (
          <div className="space-y-4 fade-in">
            {ANALYSIS_AXES.filter((a) => results[a.id]).map((axis, i) => {
              const r = results[axis.id];
              return (
                <div
                  key={axis.id}
                  className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-5"
                  style={{ animation: `fadeIn 0.4s ease ${i * 0.1}s both` }}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h3 className="text-base font-bold">{r.title || axis.name}</h3>
                    <div className="flex flex-col items-end">
                      <span
                        className="text-2xl font-bold"
                        style={{ color: scoreColor(r.score) }}
                      >
                        {r.score}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)]">/ 100</span>
                    </div>
                  </div>
                  <div
                    className="h-1.5 rounded-full bg-[var(--bg-hover)] mb-4 overflow-hidden"
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${r.score}%`,
                        background: scoreColor(r.score),
                      }}
                    />
                  </div>
                  <p className="text-[13px] font-semibold mb-2">{r.summary}</p>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-4 whitespace-pre-wrap">
                    {r.details}
                  </p>
                  {r.recommendations.length > 0 && (
                    <div className="bg-[var(--bg-elevated)] p-3 rounded-sm">
                      <div className="text-xs font-semibold mb-2">💡 改善提案</div>
                      <ul className="space-y-1 text-xs text-[var(--text-secondary)]">
                        {r.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="text-[var(--accent-green)]">✓</span>
                            <span className="leading-relaxed">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!running && !results && (
          <EmptyState
            icon={Sparkles}
            title="解析結果がここに表示されます"
            description="メールと分析軸を選んで実行ボタンをクリック"
          />
        )}
      </div>
    </Screen>
  );
}
